import styled from "styled-components";
import React, { useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Option,
  Spinner,
} from "@fluentui/react-components";
import { Star16Filled } from "@fluentui/react-icons";
import { Setting } from "types/types";
import { ColumnOptionsContext } from "./ColumnOptions";

const DEFAULT_VIEW = "__default__";

export const ViewSelect = ({ className }: { className?: string }) => {
  const {
    views,
    selectedView,
    isViewLoading,
    onChangeView,
    strings,
    hideViewOptionsFeatures,
    usePlaceholder,
    hasUnsavedChanges,
    id,
  } = useContext(ColumnOptionsContext);
  // Holds the view the user asked to switch to while there are unsaved changes.
  const [pendingViewId, setPendingViewId] = useState<{
    viewId: Setting["userAppSettingId"] | null;
  } | null>(null);

  if (hideViewOptionsFeatures) return null;

  const selectedKey = selectedView
    ? String(selectedView.userAppSettingId)
    : usePlaceholder
    ? undefined
    : DEFAULT_VIEW;

  const requestChangeView = (viewId: Setting["userAppSettingId"] | null) => {
    if (viewId === (selectedView?.userAppSettingId ?? null)) return;
    if (hasUnsavedChanges) {
      setPendingViewId({ viewId });
      return;
    }
    onChangeView(viewId);
  };

  return (
    <ViewSelectContainer
      className={className}
      data-testid="column-options-view-select"
    >
      <Dropdown
        id={id ? `${id}-view-select` : undefined}
        aria-label={strings.viewSelectPlaceholder}
        placeholder={strings.viewSelectPlaceholder}
        disabled={isViewLoading}
        value={
          selectedView
            ? selectedView.userAppSettingName
            : selectedKey === DEFAULT_VIEW
            ? strings.defaultViewLabel
            : ""
        }
        selectedOptions={selectedKey ? [selectedKey] : []}
        expandIcon={isViewLoading ? <Spinner size="extra-tiny" /> : undefined}
        onOptionSelect={(_, data) => {
          if (!data.optionValue) return;
          requestChangeView(
            data.optionValue === DEFAULT_VIEW ? null : Number(data.optionValue)
          );
        }}
        style={{ width: "100%", minWidth: 0 }}
      >
        {!usePlaceholder && (
          <Option value={DEFAULT_VIEW}>{strings.defaultViewLabel}</Option>
        )}
        {views.length === 0 && usePlaceholder && (
          <Option value="" disabled>
            {strings.viewSelectNoViewsLabel}
          </Option>
        )}
        {views.map((view) => (
          <Option
            key={view.userAppSettingId}
            value={String(view.userAppSettingId)}
            text={view.userAppSettingName}
          >
            <SelectedValue>
              {view.isDefault && <StarIcon />}
              {view.userAppSettingName}
            </SelectedValue>
          </Option>
        ))}
      </Dropdown>
      <Dialog
        open={pendingViewId !== null}
        onOpenChange={(_, data) => !data.open && setPendingViewId(null)}
        modalType="alert"
      >
        <DialogSurface data-testid="column-options-discard-dialog">
          <DialogBody>
            <DialogTitle>{strings.unsavedChangesLabel}</DialogTitle>
            <DialogContent>{strings.discardChangesConfirm}</DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setPendingViewId(null)}
              >
                {strings.cancelButtonLabel}
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  if (pendingViewId) onChangeView(pendingViewId.viewId);
                  setPendingViewId(null);
                }}
              >
                {strings.discardButtonLabel}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </ViewSelectContainer>
  );
};

const ViewSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectedValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const StarIcon = styled(Star16Filled)`
  flex-shrink: 0;
  color: ${(props) => props.theme.palette.themePrimary};
`;
